/**
 * MeasureAngleSnapping - 90° angle snapping for measure tools.
 *
 * Handles angle snapping during both drawing (new points) and drag editing
 * (existing points). Owns the helper line used for 3D H/V snap visualization.
 */

import * as THREE from 'three';
import { SphereMarker } from '../../../../utils/SphereMarker';
import { HelperLine } from '../../../../utils/HelperLine';
import { MeasureTools } from '../../../../utils/Measure';
import { PointoramaAnnotation } from '../../../../utils/PointoramaAnnotation';
import { projectOnLine, findLineIntersection } from './GeometryUtils';

/** A plane-like drawing target — only its normal is needed for angle snapping.
 *  Satisfied by both PointoramaPlane and PointoramaSurface. */
type DrawingPlane = { getNormal(): THREE.Vector3 };

interface Point {
  position: THREE.Vector3;
}

export class MeasureAngleSnapping {
  private helperLine!: HelperLine;

  /** Add helper line to scene. */
  init(scene: THREE.Scene): void {
    this.helperLine = new HelperLine();
    this.helperLine.visible = false;
    scene.add(this.helperLine);
  }

  /** Remove helper line from scene. */
  dispose(scene: THREE.Scene): void {
    scene.remove(this.helperLine);
  }

  /**
   * Reset helper line visibility. Call once per frame before snapping runs.
   * `snapToHorizontalOrVertical` will set it visible when active.
   */
  resetHelperLine(): void {
    this.helperLine.visible = false;
  }

  /**
   * Snap a new point to 90° angles during drawing.
   *
   * For the first line (2 points): snaps to absolute horizontal/vertical on the plane.
   * For subsequent lines (3+ points): snaps relative to the previous segment
   * (parallel, perpendicular, or parallel-to-first-segment).
   * For 4+ points: also checks closing snap candidates.
   */
  computeSnappedAnglePoint(
    drawingPlane: DrawingPlane,
    mousePoint: THREE.Vector3,
    measurePoints: Point[],
    getSnapPoint: (origin: THREE.Vector3, direction: THREE.Vector3) => THREE.Vector3 | null,
    camera: THREE.Camera,
  ): THREE.Vector3 | undefined {
    const points = measurePoints.map((p) => p.position);
    if (points.length < 2) return undefined;

    if (points.length === 2) {
      // First line: snap to absolute 90° angles (horizontal/vertical relative to drawing plane)
      const start = points.at(-2);
      if (!start) return undefined;

      const planeNormal = drawingPlane.getNormal();

      // Choose reference vector that isn't parallel to the plane normal
      const isTopDown = Math.abs(planeNormal.z) > 0.9;
      const referenceUp = isTopDown ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);

      const directionHorizontal = new THREE.Vector3().crossVectors(planeNormal, referenceUp).normalize();
      const directionVertical = new THREE.Vector3().crossVectors(planeNormal, directionHorizontal).normalize();

      const horizontalPoint = getSnapPoint(start, directionHorizontal);
      const verticalPoint = getSnapPoint(start, directionVertical);
      if (!horizontalPoint || !verticalPoint) return undefined;

      return horizontalPoint.distanceTo(mousePoint) < verticalPoint.distanceTo(mousePoint) ? horizontalPoint : verticalPoint;
    }

    // Subsequent lines: snap to 90° angles relative to the previous line segment
    const start = points.at(-3);
    const corner = points.at(-2);
    if (!start || !corner) return undefined;

    const previousDirection = new THREE.Vector3().subVectors(corner, start).normalize();
    const perpendicularDirection = new THREE.Vector3().crossVectors(previousDirection, drawingPlane.getNormal()).normalize();

    const candidateDirections: THREE.Vector3[] = [previousDirection, perpendicularDirection];

    // For polygons with 4+ points, add additional snap options and check closing snaps
    if (points.length >= 4) {
      const firstPoint = points[0];
      const secondPoint = points[1];

      const firstSegmentDirection = new THREE.Vector3().subVectors(secondPoint, firstPoint).normalize();
      candidateDirections.push(firstSegmentDirection);

      // Check for closing snap points for ALL candidate directions
      for (const direction of candidateDirections) {
        const closingSnapPoint = projectOnLine(firstPoint, corner, direction);
        const closingSnapScreenPos = closingSnapPoint.clone().project(camera);
        const mouseScreenPos = mousePoint.clone().project(camera);
        const screenDistance = new THREE.Vector2(closingSnapScreenPos.x - mouseScreenPos.x, closingSnapScreenPos.y - mouseScreenPos.y).length();

        // Threshold in normalized device coordinates (~20-30 pixels)
        if (screenDistance < 0.05) {
          return closingSnapPoint;
        }
      }
    }

    // Build candidate points from directions
    const candidates: THREE.Vector3[] = [];
    for (const direction of candidateDirections) {
      const point = getSnapPoint(corner, direction);
      if (point) {
        candidates.push(point);
      }
    }

    if (candidates.length === 0) return undefined;

    // Return the candidate closest to the mouse cursor
    let bestCandidate = candidates[0];
    let bestDistance = bestCandidate.distanceTo(mousePoint);
    for (let i = 1; i < candidates.length; i++) {
      const distance = candidates[i].distanceTo(mousePoint);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestCandidate = candidates[i];
      }
    }
    return bestCandidate;
  }

  /**
   * Snap when dragging an existing point to maintain 90° angles at adjacent corners.
   *
   * When dragging point B in shape ...A-B-C...:
   * - Find the intersection of line from A perpendicular to edge into A
   *   and line from C perpendicular to edge out of C
   * - This creates 90° angles at both A and C
   * - Uses screen-space distance threshold to determine if snap should activate
   */
  getSnappedAnglePointForDrag(drawingPlane: DrawingPlane, mousePoint: THREE.Vector3 | null, draggedSphere: SphereMarker, camera: THREE.Camera): THREE.Vector3 | undefined {
    if (!mousePoint) return undefined;

    const measure = draggedSphere.parent as PointoramaAnnotation;
    if (!measure) return undefined;

    const points = measure.points.map((p) => p.position);
    const draggedIndex = measure.spheres.indexOf(draggedSphere);
    if (draggedIndex < 0 || points.length < 2) return undefined;

    const planeNormal = drawingPlane.getNormal();
    const mouseScreenPos = mousePoint.clone().project(camera);

    // Get adjacent points (wrap around for closed polygons like AreaAnnotation)
    const isClosedPolygon = measure.type === MeasureTools.AREA;

    const getIndex = (idx: number): number => {
      if (idx < 0) return isClosedPolygon ? points.length + idx : -1;
      if (idx >= points.length) return isClosedPolygon ? idx - points.length : -1;
      return idx;
    };

    const prevIndex = getIndex(draggedIndex - 1);
    const nextIndex = getIndex(draggedIndex + 1);
    const prevPrevIndex = getIndex(draggedIndex - 2);
    const nextNextIndex = getIndex(draggedIndex + 2);

    const checkSnapPoint = (snapPoint: THREE.Vector3): THREE.Vector3 | undefined => {
      const snapScreenPos = snapPoint.clone().project(camera);
      const screenDistance = new THREE.Vector2(snapScreenPos.x - mouseScreenPos.x, snapScreenPos.y - mouseScreenPos.y).length();

      if (screenDistance < 0.05) {
        return snapPoint;
      }
      return undefined;
    };

    // Get absolute directions for fallback
    const isTopDown = Math.abs(planeNormal.z) > 0.9;
    const referenceUp = isTopDown ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    const absoluteDirH = new THREE.Vector3().crossVectors(planeNormal, referenceUp).normalize();
    const absoluteDirV = new THREE.Vector3().crossVectors(planeNormal, absoluteDirH).normalize();

    // Try to find intersection point where both adjacent edges would be at 90°
    if (prevIndex >= 0 && nextIndex >= 0) {
      const A = points[prevIndex];
      const C = points[nextIndex];

      let dirFromA: THREE.Vector3;
      if (prevPrevIndex >= 0 && prevPrevIndex !== draggedIndex) {
        const prevPrev = points[prevPrevIndex];
        const incomingDir = new THREE.Vector3().subVectors(A, prevPrev).normalize();
        dirFromA = new THREE.Vector3().crossVectors(incomingDir, planeNormal).normalize();
      } else {
        dirFromA = absoluteDirH;
      }

      let dirFromC: THREE.Vector3;
      if (nextNextIndex >= 0 && nextNextIndex !== draggedIndex) {
        const nextNext = points[nextNextIndex];
        const outgoingDir = new THREE.Vector3().subVectors(nextNext, C).normalize();
        dirFromC = new THREE.Vector3().crossVectors(outgoingDir, planeNormal).normalize();
      } else {
        dirFromC = absoluteDirV;
      }

      const intersection = findLineIntersection(A, dirFromA, C, dirFromC, planeNormal);
      if (intersection) {
        const result = checkSnapPoint(intersection);
        if (result) return result;
      }

      // Also try with parallel directions (for 0°/180° snaps)
      if (prevPrevIndex >= 0 && prevPrevIndex !== draggedIndex) {
        const prevPrev = points[prevPrevIndex];
        const parallelDirA = new THREE.Vector3().subVectors(A, prevPrev).normalize();
        const intersection2 = findLineIntersection(A, parallelDirA, C, dirFromC, planeNormal);
        if (intersection2) {
          const result = checkSnapPoint(intersection2);
          if (result) return result;
        }
      }

      if (nextNextIndex >= 0 && nextNextIndex !== draggedIndex) {
        const nextNext = points[nextNextIndex];
        const parallelDirC = new THREE.Vector3().subVectors(nextNext, C).normalize();
        const intersection3 = findLineIntersection(A, dirFromA, C, parallelDirC, planeNormal);
        if (intersection3) {
          const result = checkSnapPoint(intersection3);
          if (result) return result;
        }
      }
    }

    // Fallback: snap from individual points
    if (prevIndex >= 0) {
      const A = points[prevIndex];
      let directions: THREE.Vector3[];
      if (prevPrevIndex >= 0 && prevPrevIndex !== draggedIndex) {
        const prevPrev = points[prevPrevIndex];
        const incomingDir = new THREE.Vector3().subVectors(A, prevPrev).normalize();
        const perpDir = new THREE.Vector3().crossVectors(incomingDir, planeNormal).normalize();
        directions = [perpDir, incomingDir];
      } else {
        directions = [absoluteDirH, absoluteDirV];
      }

      for (const dir of directions) {
        const snapPoint = projectOnLine(mousePoint, A, dir);
        const result = checkSnapPoint(snapPoint);
        if (result) return result;
      }
    }

    if (nextIndex >= 0) {
      const C = points[nextIndex];
      let directions: THREE.Vector3[];
      if (nextNextIndex >= 0 && nextNextIndex !== draggedIndex) {
        const nextNext = points[nextNextIndex];
        const outgoingDir = new THREE.Vector3().subVectors(nextNext, C).normalize();
        const perpDir = new THREE.Vector3().crossVectors(outgoingDir, planeNormal).normalize();
        directions = [perpDir, outgoingDir];
      } else {
        directions = [absoluteDirH, absoluteDirV];
      }

      for (const dir of directions) {
        const snapPoint = projectOnLine(mousePoint, C, dir);
        const result = checkSnapPoint(snapPoint);
        if (result) return result;
      }
    }

    return undefined;
  }

  /**
   * In 3D mode: snap to horizontal or vertical relative to previous point's Z.
   * Returns the snapped point and updates the helper line visualization.
   */
  snapToHorizontalOrVertical(point: THREE.Vector3, measurePoints: Point[]): THREE.Vector3 {
    const points = measurePoints.map((p) => p.position);
    if (points.length < 2) return point;

    const previousPoint = points[points.length - 2];

    const horizontalPoint = new THREE.Vector3(point.x, point.y, previousPoint.z);
    const verticalPoint = new THREE.Vector3(previousPoint.x, previousPoint.y, point.z);
    if (verticalPoint.distanceTo(point) < horizontalPoint.distanceTo(point)) {
      this.helperLine.setBetweenPoints([point, verticalPoint]);
      this.helperLine.visible = true;
      return verticalPoint;
    } else {
      this.helperLine.setBetweenPoints([point, horizontalPoint]);
      this.helperLine.visible = true;
      return horizontalPoint;
    }
  }
}
